// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAccountForm } from '@client/banks/hooks/use-account-form';
import { useBankCurrencyOptions } from '@client/banks/hooks/use-bank-currency-options';
import type { BankAccount } from '@client/banks/types/account';
import { buildUpdateAccountPayload } from '@client/banks/utils/build-update-advisory-accounts-citec-payload';
import {
  createEmptyBankAccount,
  getDefaultBankCurrency,
} from '@client/banks/utils/bank-form-defaults';
import { mapAdvisoryBankAccountToBankAccount } from '@client/banks/utils/map-advisory-bank-account-to-bank-account';
import { mapAdvisoryBankToUpdateInstitution } from '@client/banks/utils/map-advisory-bank-to-update-institution';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import type {
  AdvisoryBank,
  AdvisoryBankAccount,
} from '@shared/data-access/types/brokerage-types';
import { useCallback, useMemo } from 'react';

export const BANK_ACCOUNT_UPDATE_FORM_ID = 'bank-account-update-form';

export interface BankAccountUpdateDrawerProps {
  readonly institution: AdvisoryBank | null;
  readonly account: AdvisoryBankAccount | null;
  readonly open: boolean;
  readonly onClose: () => void;
}

export const useBankAccountUpdateDrawer = ({
  institution,
  account,
  open,
  onClose,
}: BankAccountUpdateDrawerProps) => {
  const currencyOptions = useBankCurrencyOptions();

  const defaultValues = useMemo(() => {
    if (!account) {
      return createEmptyBankAccount(getDefaultBankCurrency(currencyOptions));
    }

    return mapAdvisoryBankAccountToBankAccount(account);
  }, [account, currencyOptions]);

  const form = useAccountForm({
    variant: 'update',
    defaultValues,
    disabled: !open,
  });

  const [updateAccountsCitec, { isLoading: isSaveLoading }] =
    advisoryApi.useUpdateAdvisoryAccountsCitecMutation();

  const institutionSnapshot = useMemo(
    () => (institution ? mapAdvisoryBankToUpdateInstitution(institution) : null),
    [institution],
  );

  const onSubmit = useCallback(
    async (data: BankAccount) => {
      if (!institutionSnapshot) {
        return;
      }

      await updateAccountsCitec(
        buildUpdateAccountPayload(institutionSnapshot, data),
      ).unwrap();

      onClose();
    },
    [institutionSnapshot, onClose, updateAccountsCitec],
  );

  const onSave = useCallback(() => {
    void form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const isSaveDisabled = useMemo(
    () => !form.formState.isDirty || !form.formState.isValid,
    [form.formState.isDirty, form.formState.isValid],
  );

  const bankName = institution?.name ?? '';

  return {
    institution,
    account,
    bankName,
    currencyOptions,
    form,
    onSubmit,
    onSave,
    isSaveDisabled,
    isSaveLoading,
  };
};
